import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, SxProps, Theme, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { UPLOAD_ACCEPT, UPLOAD_MAX_SIZE } from "constants/upload";
import ButtonComponent from "../button/ButtonComponent";

interface UploadImageProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  accept?: string;
  maxSize?: number;
  width?: string | number;
  height?: string | number;
  disabled?: boolean;
  cacheBuster?: number; // Add cache buster prop
  sxBox?: SxProps<Theme>;
  onImageChange?: (file: File | null) => void;
  onRemoveImage: () => void;
}

/**
 * UploadImage Component
 *
 * A generic React component for uploading and previewing images, integrated with react-hook-form.
 * Supports drag-and-drop, file size/type restrictions, image preview, and removal.
 * @template T - The type of form field values (extends FieldValues).
 * @param {object} props - Component props.
 * @param {Path<T>} props.name - The name of the form field.
 * @param {Control<T>} props.control - The react-hook-form control object.
 * @param {string} [props.accept] - Accepted file types (default: all images).
 * @param {number} [props.maxSize] - Maximum file size in bytes (default: 5MB).
 * @param {string|number} [props.width] - Width of the upload area.
 * @param {string|number} [props.height] - Height of the upload area.
 * @param {boolean} [props.disabled] - Whether the upload is disabled.
 * @param {SxProps<Theme>} [props.sxBox] - Additional styles for the upload area.
 * @param {(file: File|null) => void} [props.onImageChange] - Callback when the image changes.
 */
function UploadImage<T extends FieldValues>({
  name,
  control,
  accept = UPLOAD_ACCEPT.IMAGE.MIME_TYPES,
  maxSize = UPLOAD_MAX_SIZE.IMAGE,
  width = 190,
  height = 140,
  disabled = false,
  cacheBuster,
  onImageChange,
  onRemoveImage,
  sxBox,
}: UploadImageProps<T>) {
  const [preview, setPreview] = useState<string | null>(null);
  const onChangeRef = useRef<((value: File | null) => void) | null>(null);
  const lastValueRef = useRef<File | null>(null);

  // Helper function to add cache buster to image URL
  const getImageUrlWithCacheBuster = useCallback(
    (url: string | null) => {
      if (!url || !cacheBuster) return url;
      const separator = url.includes("?") ? "&" : "?";
      return `${url}${separator}t=${cacheBuster}`;
    },
    [cacheBuster],
  );

  const handleFileUpload = useCallback(
    (file: File | null) => {
      if (file) {
        // Check if file has a preview URL (for existing images)
        const { previewUrl } = file as any;
        if (previewUrl) {
          setPreview(getImageUrlWithCacheBuster(previewUrl));
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            setPreview(reader.result as string);
          };
          reader.readAsDataURL(file);
        }
        onChangeRef.current?.(file);
        onImageChange?.(file);
      } else {
        setPreview(null);
        onChangeRef.current?.(null);
        onImageChange?.(null);
      }
    },
    [onImageChange, getImageUrlWithCacheBuster],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        handleFileUpload(acceptedFiles[0]);
      }
    },
    [handleFileUpload],
  );

  // Convert accept string to dropzone format
  const dropzoneAccept = useMemo(() => {
    if (!accept || accept === UPLOAD_ACCEPT.IMAGE.MIME_TYPES) {
      return UPLOAD_ACCEPT.IMAGE.DROPZONE_FORMAT;
    }

    // Parse accept string like "image/jpeg, image/jpg, image/png"
    const acceptTypes = accept.split(",").map((type) => type.trim());
    const acceptObject: Record<string, string[]> = {};

    acceptTypes.forEach((type) => {
      if (type.startsWith("image/")) {
        const extension = type.replace("image/", ".");
        if (!acceptObject["image/*"]) {
          acceptObject["image/*"] = [];
        }
        acceptObject["image/*"].push(extension);
      }
    });

    return acceptObject;
  }, [accept]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: dropzoneAccept,
    maxSize,
    multiple: false,
    disabled,
  });

  const handleRemoveImage = useCallback(() => {
    // handleFileUpload(null);
    onRemoveImage();
  }, [handleFileUpload]);

  const updatePreview = useCallback(
    (value: File | null) => {
      if (value !== lastValueRef.current) {
        setTimeout(() => {
          const previewUrl = (value as any)?.previewUrl;
          if (previewUrl) {
            setPreview(getImageUrlWithCacheBuster(previewUrl));
          } else {
            setPreview(null);
          }
          lastValueRef.current = value;
        }, 0);
      }
    },
    [getImageUrlWithCacheBuster],
  );

  // Initialize preview when component mounts with default value
  useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    const currentValue = control._formValues[name];
    if (currentValue && (currentValue as any).previewUrl) {
      setPreview(getImageUrlWithCacheBuster((currentValue as any).previewUrl));
    }
    // eslint-disable-next-line no-underscore-dangle
  }, [control._formValues, name, getImageUrlWithCacheBuster]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => {
        // Update the ref with current onChange
        onChangeRef.current = onChange;

        // Initialize preview if value has previewUrl and it's different from last value
        // if (value !== lastValueRef.current) {
        //   setPreview((value as any)?.previewUrl || null);
        //   lastValueRef.current = value;
        // }
        updatePreview(value);

        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            {/* Upload Area */}
            <Box
              {...getRootProps()}
              sx={{
                width,
                height,
                // border: preview ? "2px solid var(--gray-250)" : "2px dashed var(--gray-300)",
                border: "2px solid var(--gray-250)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                cursor: disabled ? "not-allowed" : "pointer",
                backgroundColor: "var(--white)",
                transition: "all 0.2s ease-in-out",
                position: "relative",
                "&:hover":
                  !disabled && !preview
                    ? {
                        backgroundColor: "var(--offwhite-150)",
                        borderColor: "var(--primary-400)",
                      }
                    : {},
                ...sxBox,
              }}
            >
              <input {...getInputProps()} />

              {preview ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "6px",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={preview}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: isDragActive ? "var(--primary-600)" : "var(--gray-400)",
                      fontSize: { xs: 16, lg: 18 },
                      fontWeight: 400,
                      textAlign: "center",
                    }}
                  >
                    ドラッグ＆ドロップ
                  </Typography>
                </Box>
              )}

              {isDragActive && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 204, 0, 0.1)",
                    border: "2px dashed var(--primary-400)",
                    borderRadius: "6px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: "var(--primary-600)",
                      fontSize: "16px",
                      fontWeight: 500,
                    }}
                  >
                    ここにドロップ
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Delete Button */}
            {preview && (
              <ButtonComponent
                label="詳細画像削除"
                onClick={handleRemoveImage}
                sx={{
                  fontSize: { xs: 16, md: 20 },
                  height: { xs: "36px", lg: "40px" },
                  width: { xs: 132, md: 172 },
                }}
              />
            )}
          </Box>
        );
      }}
    />
  );
}

export default UploadImage;
